import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import dayjs from 'dayjs';

import { ShareAccessToken } from 'models';

import { AnonymousUserError, setAccessTokenReduxShim, setShareTokenReduxShim } from 'core/api';
import { DataStream, parseJwt } from 'core/utils';

import { AuthenticationScheme, SHARE_TOKEN_SESSION_STORAGE_KEY } from '../constants';
import { AuthenticationManagerHandle } from '../types/AuthenticationManagerHandle';
import { IAuthenticationScheme } from '../types/IAuthenticationScheme';
import { ParsedShareAccessToken } from '../types/ParsedShareAccessToken';

export class ShareAuthenticationScheme implements IAuthenticationScheme {
  public readonly hub: {
    shareTokenGetter: DataStream<(() => string) | null>;
    parsedShareToken: DataStream<ParsedShareAccessToken | null>;
  };

  constructor(private manager: AuthenticationManagerHandle) {
    this.initialize = this.initialize.bind(this);
    this.setActiveScheme = this.setActiveScheme.bind(this);
    this.login = this.login.bind(this);
    this.logout = this.logout.bind(this);
    this.shareTokenGetter = this.shareTokenGetter.bind(this);
    this.parseShareJwt = this.parseShareJwt.bind(this);

    this.hub = {
      shareTokenGetter: new DataStream<(() => string) | null>(null),
      parsedShareToken: new DataStream<ParsedShareAccessToken | null>(null),
    };
  }

  public async initialize() {
    const shareToken = sessionStorage.getItem(SHARE_TOKEN_SESSION_STORAGE_KEY);

    if (shareToken == null) {
      this.hub.parsedShareToken.emit(null);
      return false;
    }

    const parsed = this.parseShareJwt(shareToken);
    const now = dayjs.utc();

    // Discard expired tokens.
    if (parsed.exp.diff(now) <= 0) {
      sessionStorage.removeItem(SHARE_TOKEN_SESSION_STORAGE_KEY);
      this.hub.parsedShareToken.emit(null);
      return false;
    }

    // Validate that the token is for the current share link
    const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
    const linkIdRegex = /\/share\/([^/]+)/i;
    const linkIdMatch = linkIdRegex.exec(currentPath);
    const currentLinkId = linkIdMatch ? linkIdMatch[1] : null;

    // If we can determine the current linkId and it doesn't match the token's linkId,
    // discard the token
    if (currentLinkId && parsed.shareLinkId !== currentLinkId) {
      console.log('ShareAuthenticationScheme.initialize: Token linkId mismatch', {
        tokenLinkId: parsed.shareLinkId,
        currentLinkId: currentLinkId,
      });

      sessionStorage.removeItem(SHARE_TOKEN_SESSION_STORAGE_KEY);
      this.hub.parsedShareToken.emit(null);
      return false;
    }

    this.hub.parsedShareToken.emit(parsed);
    return true;
  }

  public setActiveScheme() {
    this.manager.apiClient.setAccessTokenFunction(null);
    setAccessTokenReduxShim(null);
    this.manager.apiClient.setShareTokenFunction(this.shareTokenGetter);
    setShareTokenReduxShim(this.shareTokenGetter);

    const token = this.hub.parsedShareToken.getCurrentValue()!;
    const datadogUser = {
      id: token.shareLinkId,
      name: `Share ${token.shareLinkId}`,
      scheme: 'share',
      shareType: token.shareType,
      sharePatientId: token.sharePatientId,
    };
    datadogRum.setUser(datadogUser);
    datadogLogs.setUser(datadogUser);

    return true;
  }

  public async login(linkId: string, password: string | null, hipaaConsentAccepted: boolean, userClaims?: Record<string, string>) {
    const response = await this.manager.apiClient.studyShare.authenticate(linkId, password, hipaaConsentAccepted, userClaims);

    if (!response.success) return response;

    sessionStorage.setItem(SHARE_TOKEN_SESSION_STORAGE_KEY, response.accessToken);

    const shareToken = this.parseShareJwt(response.accessToken);

    this.hub.parsedShareToken.emit(shareToken);
    this.setActiveScheme();
    this.manager.setActiveScheme(AuthenticationScheme.SHARE);

    return response;
  }

  public logout() {
    datadogRum.clearUser();
    datadogLogs.clearUser();
    datadogRum.clearGlobalContext();
    datadogLogs.clearGlobalContext();
    datadogRum.stopSession();

    sessionStorage.removeItem(SHARE_TOKEN_SESSION_STORAGE_KEY);
    this.hub.parsedShareToken.emit(null);
    this.manager.setActiveSchemeAnonymous();
  }

  private async shareTokenGetter() {
    const accessToken = sessionStorage.getItem(SHARE_TOKEN_SESSION_STORAGE_KEY);

    if (accessToken == null) {
      // No token found - switch to anonymous mode to prevent infinite loops
      this.hub.parsedShareToken.emit(null);
      this.manager.setActiveSchemeAnonymous();
      throw new AnonymousUserError('Could not find share JWT access token in session storage.');
    }

    // Validate that the token is for the current share link
    try {
      const parsed = this.parseShareJwt(accessToken);

      // Extract linkId from current URL
      const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
      const linkIdRegex = /\/share\/([^/]+)/i;
      const linkIdMatch = linkIdRegex.exec(currentPath);
      const currentLinkId = linkIdMatch ? linkIdMatch[1] : null;

      // If we can determine the current linkId and it doesn't match the token's linkId,
      // throw an error to force re-authentication
      if (currentLinkId && parsed.shareLinkId !== currentLinkId) {
        console.log('ShareTokenGetter: Token linkId mismatch', {
          tokenLinkId: parsed.shareLinkId,
          currentLinkId: currentLinkId,
        });

        // Clear the invalid token and switch to anonymous mode
        sessionStorage.removeItem(SHARE_TOKEN_SESSION_STORAGE_KEY);
        this.hub.parsedShareToken.emit(null);
        this.manager.setActiveSchemeAnonymous();

        throw new AnonymousUserError('Share token is for a different share link.');
      }

      // Check if token is expired
      const now = dayjs.utc();
      if (parsed.exp.diff(now) <= 0) {
        console.log('ShareTokenGetter: Token expired');

        // Clear the expired token and switch to anonymous mode
        sessionStorage.removeItem(SHARE_TOKEN_SESSION_STORAGE_KEY);
        this.hub.parsedShareToken.emit(null);
        this.manager.setActiveSchemeAnonymous();

        throw new AnonymousUserError('Share token has expired.');
      }
    } catch (error) {
      // If parsing fails or validation fails, clear the token, switch to anonymous mode, and throw error
      sessionStorage.removeItem(SHARE_TOKEN_SESSION_STORAGE_KEY);
      this.hub.parsedShareToken.emit(null);
      this.manager.setActiveSchemeAnonymous();

      // Re-throw the error if it's already an AnonymousUserError (from our validation above)
      if (error instanceof AnonymousUserError) {
        throw error;
      }

      throw new AnonymousUserError('Invalid share token.');
    }

    return accessToken;
  }

  public parseShareJwt(shareToken: string): ParsedShareAccessToken {
    const {
      sub,
      nbf,
      exp,
      iat,
      'share-link-id': shareLinkId,
      'share-type': shareType,
      'share-patient-id': sharePatientId,
      'share-message': message,
      ...restParsed
    } = parseJwt<ShareAccessToken>(shareToken);

    const subject = parseInt(sub, 10);
    const notBeforeTime = dayjs.utc(nbf * 1000);
    const expiration = dayjs.utc(exp * 1000);
    const issuedAtTime = dayjs.utc(iat * 1000);

    return {
      ...restParsed,
      shareLinkId,
      shareType,
      sharePatientId,
      message: message ?? null,
      sub: subject,
      nbf: notBeforeTime,
      exp: expiration,
      iat: issuedAtTime,
    };
  }
}
